import { AccordionAnimation } from "components/AccordionAnimation";
import { TableUI } from "components/design/TableUI";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { AnimatePresence } from "framer-motion";
import { useState } from "react";
import { ColumnDef, getCoreRowModel, useReactTable } from "react-table-8.10.7";
import { useGetLedgerLinkedTxnsQuery } from "store/apis/generalLedger";
import { Transactions } from "types/Models/books";
import { pluralize } from "utils/pluralize";

type LinkedTransactionsProps = {
  ledgerEntryId: string;
  entityId: string;
  columns: ColumnDef<Transactions, any>[];
};

export default function LinkedTransactions({
  ledgerEntryId,
  entityId,
  columns,
}: LinkedTransactionsProps) {
  const [pageNum, setPageNum] = useState(1);

  const {
    data: linkedTransactions,
    isLoading,
    isSuccess,
  } = useGetLedgerLinkedTxnsQuery(
    {
      entityId,
      ledgerEntryId: ledgerEntryId,
      page_num: pageNum,
    },
    {
      skip: !ledgerEntryId || !entityId,
    }
  );

  const {
    linked_transactions = [],
    per_page = 10,
    total_count = 0,
    total_pages = 1,
  } = linkedTransactions || {};

  const table = useReactTable({
    data: linked_transactions,
    getCoreRowModel: getCoreRowModel(),
    columns,
    defaultColumn: {
      minSize: 10,
    },
  });

  if (linked_transactions.length === 0) {
    return null;
  }

  return (
    <Accordion.Root type="single" collapsible>
      <Accordion.Item value="LINKED_TRANSACTIONS">
        <Accordion.Trigger disabled={isLoading || !isSuccess}>
          Linked to {pluralize(total_count || 0, "transaction", "transactions")}
        </Accordion.Trigger>
        <Accordion.Content>
          <AnimatePresence>
            <AccordionAnimation duration={0.3}>
              <div className="t-flex t-flex-col t-gap-3">
                <div className="t-flex t-items-center t-justify-end">
                  <PaginationHandler
                    page_num={pageNum}
                    per_page={per_page}
                    total_count={total_count}
                    total_pages={total_pages}
                    onPageNumChange={setPageNum}
                  />
                </div>
                <div className="t-overflow-hidden">
                  <TableUI table={table} />
                </div>
              </div>
            </AccordionAnimation>
          </AnimatePresence>
        </Accordion.Content>
      </Accordion.Item>
    </Accordion.Root>
  );
}
